import React, { useState, useEffect } from 'react';
import {
        Box,
        Card,
        CardContent,
        Typography,
        Button,
        IconButton,
        Stack,
        Chip,
        Dialog,
        DialogTitle,
        DialogContent,
        CircularProgress
} from '@mui/material';
import {
        ArrowBack as LeftIcon,
        ArrowForward as RightIcon,
        CheckCircle as DoneIcon,
        AttachMoney as BusinessIcon,
        Person as PersonalIcon
} from '@mui/icons-material';
import Confetti from 'react-confetti';
import axios from 'axios';

interface ExpenseItem {
        id: string;
        merchant: string;
        amount: number;
        date: string;
        description: string;
}

interface ExpenseSwiperProps {
        open: boolean;
        onClose: () => void;
        onComplete: () => void;
}

export const ExpenseSwiper: React.FC<ExpenseSwiperProps> = ({ open, onClose, onComplete }) => {
        const [expenses, setExpenses] = useState<ExpenseItem[]>([]);
        const [currentIndex, setCurrentIndex] = useState(0);
        const [direction, setDirection] = useState<'left' | 'right' | null>(null);
        const [completed, setCompleted] = useState(false);
        const [loading, setLoading] = useState(true);

        useEffect(() => {
                if (open) {
                        fetchTransactions();
                }
        }, [open]);

        const fetchTransactions = async () => {
                try {
                        setLoading(true);
                        const res = await axios.get('/api/cfo/transactions/uncategorized/test-customer-123');
                        setExpenses(res.data);
                        setCurrentIndex(0);
                        setCompleted(false);
                } catch (err) {
                        console.error("Failed to fetch expenses", err);
                } finally {
                        setLoading(false);
                }
        };

        const handleSwipe = async (dir: 'left' | 'right') => {
                if (!expenses[currentIndex]) return;

                setDirection(dir);

                const category = dir === 'left' ? 'Personal' : 'Business';
                const txId = expenses[currentIndex].id;

                // Optimistic UI update - simulate animation time
                setTimeout(async () => {
                        // Fire and forget API call for responsiveness
                        try {
                                await axios.post('/api/cfo/transactions/categorize/test-customer-123', {
                                        transaction_id: txId,
                                        category: category
                                });
                        } catch (e) {
                                console.error("Failed to categorize", e);
                        }

                        setDirection(null);

                        if (currentIndex < expenses.length - 1) {
                                setCurrentIndex(prev => prev + 1);
                        } else {
                                setCompleted(true);
                                // Trigger external completion after confetti starts
                                setTimeout(onComplete, 3000);
                        }
                }, 300);
        };

        if (!open) return null;

        const currentCard = expenses[currentIndex];
        // Calculate items left based on current index
        const itemsLeft = expenses.length - currentIndex;

        return (
                <Dialog
                        open={open}
                        onClose={onClose}
                        maxWidth="xs"
                        fullWidth
                        PaperProps={{
                                sx: {
                                        borderRadius: 4,
                                        minHeight: 500,
                                        bgcolor: '#f5f5f5',
                                        overflow: 'hidden'
                                }
                        }}
                >
                        {completed && <Confetti numberOfPieces={200} recycle={false} />}

                        <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
                                {loading ? 'Loading Transactions...' : (
                                        completed ? '🎉 Zero Inbox!' : '🧹 Swipe to Clean'
                                )}
                                {!completed && !loading && (
                                        <Typography variant="body2" color="text.secondary">
                                                {itemsLeft} cards remaining
                                        </Typography>
                                )}
                        </DialogTitle>

                        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>

                                {loading ? (
                                        <CircularProgress />
                                ) : completed ? (
                                        <Box sx={{ textAlign: 'center', animation: 'popIn 0.5s ease' }}>
                                                <DoneIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
                                                <Typography variant="h5" fontWeight="bold">All Caught Up!</Typography>
                                                <Typography variant="body1" sx={{ mt: 1, mb: 3 }}>
                                                        Your books are clean. +{expenses.length * 10} XP Earned.
                                                </Typography>
                                                <Button variant="contained" onClick={onClose} fullWidth>
                                                        Collect Rewards
                                                </Button>
                                        </Box>
                                ) : expenses.length === 0 ? (
                                        <Box sx={{ textAlign: 'center' }}>
                                                <Typography variant="subtitle1">No uncategorized transactions found.</Typography>
                                                <Button onClick={onClose} sx={{ mt: 2 }}>Close</Button>
                                        </Box>
                                ) : (
                                        <>
                                                {/* The Swipe Card */}
                                                <Box
                                                        sx={{
                                                                position: 'relative',
                                                                width: '100%',
                                                                height: 300,
                                                                perspective: 1000
                                                        }}
                                                >
                                                        <Card
                                                                elevation={6}
                                                                sx={{
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        borderRadius: 4,
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        transition: 'transform 0.3s ease, opacity 0.3s ease',
                                                                        transform: direction === 'left' ? 'translateX(-100px) rotate(-20deg)' :
                                                                                direction === 'right' ? 'translateX(100px) rotate(20deg)' : 'none',
                                                                        opacity: direction ? 0 : 1,
                                                                        position: 'absolute',
                                                                        zIndex: 2,
                                                                        bgcolor: 'white'
                                                                }}
                                                        >
                                                                <CardContent sx={{ textAlign: 'center' }}>
                                                                        <Typography variant="caption" sx={{ textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 1 }}>
                                                                                {currentCard.date}
                                                                        </Typography>
                                                                        <Typography variant="h4" fontWeight="bold" sx={{ mt: 1, mb: 2 }}>
                                                                                ${currentCard.amount.toFixed(2)}
                                                                        </Typography>
                                                                        <Typography variant="h6" color="primary.main">
                                                                                {currentCard.merchant}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic', fontSize: '0.8rem' }}>
                                                                                {currentCard.description}
                                                                        </Typography>
                                                                        <Chip label="Uncategorized" size="small" sx={{ mt: 2, bgcolor: '#eee' }} />
                                                                </CardContent>
                                                        </Card>

                                                        {/* Background Stack Effect */}
                                                        {itemsLeft > 1 && (
                                                                <Card
                                                                        sx={{
                                                                                width: '95%',
                                                                                height: '100%',
                                                                                position: 'absolute',
                                                                                top: 10,
                                                                                left: '2.5%',
                                                                                borderRadius: 4,
                                                                                zIndex: 1,
                                                                                bgcolor: '#e0e0e0',
                                                                                boxShadow: 'none'
                                                                        }}
                                                                />
                                                        )}
                                                </Box>

                                                {/* Controls */}
                                                <Stack direction="row" spacing={4} sx={{ mt: 4, width: '100%', justifyContent: 'center' }}>

                                                        <Stack alignItems="center">
                                                                <IconButton
                                                                        onClick={() => handleSwipe('left')}
                                                                        sx={{
                                                                                width: 60,
                                                                                height: 60,
                                                                                bgcolor: '#ffebee',
                                                                                color: '#d32f2f',
                                                                                '&:hover': { bgcolor: '#ffcdd2' },
                                                                                boxShadow: 2
                                                                        }}
                                                                >
                                                                        <PersonalIcon />
                                                                </IconButton>
                                                                <Typography variant="caption" sx={{ mt: 1, color: '#d32f2f', fontWeight: 'bold' }}>PERSONAL</Typography>
                                                        </Stack>

                                                        <Stack alignItems="center">
                                                                <IconButton
                                                                        onClick={() => handleSwipe('right')}
                                                                        sx={{
                                                                                width: 60,
                                                                                height: 60,
                                                                                bgcolor: '#e8f5e9',
                                                                                color: '#2e7d32',
                                                                                '&:hover': { bgcolor: '#c8e6c9' },
                                                                                boxShadow: 2
                                                                        }}
                                                                >
                                                                        <BusinessIcon />
                                                                </IconButton>
                                                                <Typography variant="caption" sx={{ mt: 1, color: '#2e7d32', fontWeight: 'bold' }}>BUSINESS</Typography>
                                                        </Stack>

                                                </Stack>

                                                <Typography variant="caption" sx={{ mt: 3, color: 'text.disabled' }}>
                                                        Swipe left for Personal, Right for Business
                                                </Typography>
                                        </>
                                )}
                        </DialogContent>
                </Dialog>
        );
};
